import { useEffect, useRef, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { FormAlert } from '../components/auth/FormAlert'
import { apiClient } from '../lib/api/client'
import { useAuth } from '../lib/auth/auth-context'
import { joinRequestProblemCopy } from '../lib/auth/problem-messages'
import { safeReturnTo } from '../lib/auth/return-to'

type Step = 'interstitial' | 'pending'

interface RequestToJoinLocationState {
  readonly accountName?: string
  readonly email?: string
  readonly returnTo?: string
}

interface PendingRequest {
  readonly accountName: string
  readonly createdAt: string
}

/**
 * Domain-match "request to join" + pending-approval screen (F0.7, USP-25,
 * PR-ONB-02) — the visible half of the request-to-join API (S0.10, `POST
 * /onboarding/join-requests`). Mounted at `/onboarding/join`.
 *
 * HOW THIS SCREEN IS REACHED (read this before changing the entry point):
 * the Gate 2 UX spec (§2.2 "Signup (domain-detection interstitial)") describes
 * a domain-lookup check as the user types their email, ahead of a signup
 * form. No such JSON pre-check endpoint exists (same class of gap F0.3/F0.4
 * already flagged for SSO auto-detection on `<LoginPage>`/`<CompanySsoPage>`)
 * — inventing one here would be guessing at a contract. What DOES exist is
 * the collision check on `POST /onboarding/account` itself (S0.10, ADR-0022
 * §5): submitting the create-company wizard with a verified-domain match
 * returns `409` with Problem `type: 'domain_join_available'` and the
 * matched account's display name (HARD-3: nothing else), *instead of*
 * creating a parallel account. `<CreateCompanyPage>`'s `handleCreate` detects
 * that response and navigates here with `{ accountName, email, returnTo }`
 * in router state — mirroring exactly how `<LoginPage>`'s `409
 * onboarding_required` branch steers into `<CreateCompanyPage>` itself. This
 * makes the "join vs. create" decision collision-driven (discovered at
 * submit time) rather than lookup-driven (discovered at email-entry time) —
 * a real, intentional deviation from the spec's flow diagram, forced by what
 * the backend actually exposes. Flagged to PM/EM rather than building a
 * fictional pre-check.
 *
 * SCOPE GAP — the spec's secondary CTA: the spec's interstitial calls for a
 * "[Create a new account instead]" secondary action so a contractor sharing
 * a verified domain can still get their own account. The actual
 * `domain_collision` guard in `provisional-account.ts` has no override
 * parameter — it unconditionally blocks creation on any verified-domain
 * match (see `join-request.test.ts` NT-ONB-D6: "no account created"). There
 * is currently no backend capability to honor that secondary CTA, so it is
 * NOT rendered as a button that would just 409 again; instead a neutral
 * "contact support" affordance stands in, and the gap is flagged to PM/EM as
 * a follow-up (a `domain_collision` override, or a documented product
 * decision that shared-domain contractors must go through admin invite).
 *
 * SCOPE GAP — the spec's "Resend nudge" action (§2.2, `<JoinRequestStatusBanner>`
 * §8) and a reload-safe/persistent pending-status page are NOT built: the
 * S0.10 contract has no requester-facing "read my own join request status"
 * or "resend" endpoint (`GET /onboarding/join-requests` is `user:manage`
 * admin-only — RLS-scoped to the ADMIN side, not the requester). The pending
 * confirmation shown here is therefore the immediate result of the `201`
 * response only, exactly like `<CreateCompanyPage>`'s wizard step state — it
 * does not survive a reload. A durable "check my request status any time"
 * page needs a new requester-scoped GET endpoint; flagged as a follow-up
 * rather than invented here.
 *
 * Guarded on `useAuth().status === 'onboarding_required'` for the same
 * reason as `<CreateCompanyPage>`: this is the one session shape that both
 * carries a live CSRF token for the mutating POST and has no `accountContext`
 * yet (S0.10's guard, `requireLiveSession`, accepts any live session, but
 * this SPA route is only ever reached from that specific pre-onboarding
 * state, so gating on it here catches a stale/direct-link/reloaded visit
 * with the same "sign in first" dead-end pattern).
 */
export function RequestToJoinPage() {
  const auth = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const locationState = (location.state as RequestToJoinLocationState | null) ?? null
  const accountName = locationState?.accountName
  const returnTo = locationState?.returnTo

  const [step, setStep] = useState<Step>('interstitial')
  const [pendingRequest, setPendingRequest] = useState<PendingRequest | undefined>()
  const [serverError, setServerError] = useState<
    { title: string; detail: string; showSignInLink?: boolean } | undefined
  >()
  const [submitting, setSubmitting] = useState(false)

  const pendingHeadingRef = useRef<HTMLHeadingElement | null>(null)
  const alertRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (step === 'pending') {
      pendingHeadingRef.current?.focus()
    }
  }, [step])

  useEffect(() => {
    if (serverError) {
      alertRef.current?.focus()
    }
  }, [serverError])

  async function handleRequestToJoin() {
    setServerError(undefined)
    setSubmitting(true)
    try {
      const { data, error } = await apiClient.POST('/onboarding/join-requests')
      if (data) {
        setPendingRequest({ accountName: data.accountName, createdAt: data.createdAt })
        setStep('pending')
        return
      }
      // USP-43 class: a 401 here means the pre-onboarding session's absolute
      // TTL expired mid-flow (`requireLiveSession`,
      // apps/api/src/modules/onboarding/routes.ts) — `/onboarding/join-requests`
      // is in `AUTH_SURFACE_PATHS` (auth-middleware.ts) precisely so this
      // branch, not the global `invalidateSession()` side effect, decides
      // what the user sees. See CreateCompanyPage.tsx's matching branch.
      if (error?.status === 401) {
        setServerError({
          title: 'Your session expired',
          detail: 'Please sign in again to finish your request to join.',
          showSignInLink: true,
        })
        return
      }
      const problemCopy = joinRequestProblemCopy(error?.type, error?.detail)
      setServerError({ ...problemCopy, showSignInLink: true })
    } catch {
      setServerError({ title: 'Something went wrong', detail: 'Please try again.' })
    } finally {
      setSubmitting(false)
    }
  }

  // See <CreateCompanyPage>: this page is reached by the same 409 hand-off, so
  // the dead-end must wait for the probe rather than flash during `loading`.
  if (auth.status === 'loading') {
    return null
  }

  if (auth.status !== 'onboarding_required') {
    return (
      <section className="auth-page">
        <h1 className="display-heading">Sign in to request to join</h1>
        <p>We need to confirm it&rsquo;s you before requesting to join an account.</p>
        <p>
          <Link to="/login">Sign in</Link>
        </p>
      </section>
    )
  }

  if (!accountName) {
    return (
      <section className="auth-page">
        <h1 className="display-heading">Let&rsquo;s find your company account</h1>
        <p>
          Start from creating your company account — if we find a matching account for your work
          email, we&rsquo;ll offer to request to join it there.
        </p>
        <p>
          <Link to="/onboarding/company">Create your company account</Link>
        </p>
      </section>
    )
  }

  return (
    <section className="auth-page">
      {step === 'interstitial' && (
        <>
          <h1 className="display-heading">Join {accountName}?</h1>
          <p>
            We found <strong>{accountName}</strong> already set up for your work email domain.
            Request to join, and an admin there will review your request.
          </p>
          {serverError && (
            <>
              <FormAlert ref={alertRef} title={serverError.title} detail={serverError.detail} />
              {serverError.showSignInLink && (
                <p>
                  <Link to="/login">Sign in again</Link>
                </p>
              )}
            </>
          )}
          <form
            noValidate
            onSubmit={(event) => {
              event.preventDefault()
              void handleRequestToJoin()
            }}
          >
            <div className="form-actions">
              <button type="submit" className="btn-primary" disabled={submitting}>
                {submitting ? 'Requesting…' : 'Request to join'}
              </button>
            </div>
          </form>
          <p className="form-field-hint">
            Need your own separate account instead of joining {accountName}?{' '}
            <a href="mailto:support@fictiv.com">Contact support</a>.
          </p>
        </>
      )}

      {step === 'pending' && pendingRequest && (
        <>
          <h1
            className="display-heading"
            ref={pendingHeadingRef}
            tabIndex={-1}
            id="pending-request-heading"
          >
            Your request is pending
          </h1>
          <output className="status-banner" aria-live="polite">
            <span className="status-banner-icon" aria-hidden="true">
              &#9689;
            </span>
            <div>
              <p className="status-banner-title">
                Pending approval &middot; {pendingRequest.accountName}
              </p>
              <p className="status-banner-detail">
                Requested {formatRequestedDate(pendingRequest.createdAt)}. An admin at{' '}
                {pendingRequest.accountName} needs to approve your request before you can start
                transacting. We&rsquo;ll email you once a decision is made.
              </p>
            </div>
          </output>
          <p>
            <Link to={safeReturnTo(returnTo, '/')}>Continue to Fictiv</Link>
          </p>
        </>
      )}
    </section>
  )
}

function formatRequestedDate(isoDate: string): string {
  const parsed = new Date(isoDate)
  if (Number.isNaN(parsed.getTime())) {
    return 'recently'
  }
  return parsed.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })
}
